/**
 * What the printed legend should say, and what each symbol looks like.
 *
 * TWO KINDS OF SWATCH, AND WHICH ONE IS RIGHT
 * A layer's symbology comes from one of two places, and the legend has to
 * follow whichever actually drew the map:
 *
 *  - The APP styled it. A `renderer` on the layer means this app chose the
 *    colours — directly for browser-drawn vectors, and through a generated SLD
 *    for WMS. Either way the app knows the symbol exactly, so it draws it, and
 *    a unique-value renderer becomes one row per class the way Esri shows it.
 *  - The SERVICE styled it. With no renderer the server's own cartography is
 *    on screen, and only the server can say what it looks like — ArcGIS through
 *    its `/legend` endpoint, GeoServer through `GetLegendGraphic`.
 *
 * Preferring the renderer whenever there is one is the whole rule. Asking a
 * service for a swatch of a style the app overrode would print a legend that
 * confidently disagrees with the map beside it.
 *
 * The caller decides WHICH layers appear; this module only says what they look
 * like. Visibility depends on zoom and scale bands the map owns, and threading
 * that in here would duplicate it.
 */

import { DEFAULT_RENDERER, getPointSymbol, mergeCategoryStyle } from '../layers/symbols';
import type { FillStyleId, LineStyleId } from '../layers/symbols';
import type { Layer, LayerGeometryType, LayerRenderer } from '../types';
import { OTHER_CATEGORY } from './printLegendExtent';

/** A symbol this app can draw itself, because this app chose it. */
export interface LegendSymbol {
  geometry: LayerGeometryType;
  color: string;
  fillOpacity: number;
  outlineColor: string;
  outlineWidth: number;
  pointSize: number;
  /** Id from POINT_SYMBOLS; absent means a plain circle. */
  symbolId?: string;
  rotation?: number;
  lineStyle?: LineStyleId;
  fillStyle?: FillStyleId;
  outlineStyle?: LineStyleId;
}

export interface LegendSwatch {
  /** Class name for a unique-value renderer; empty for a single symbol. */
  label: string;
  /** A raster swatch the service published, already decoded. */
  image?: HTMLImageElement;
  /** Drawn from the app's own renderer. */
  symbol?: LegendSymbol;
  /**
   * The swatch is almost entirely white, so it needs something behind it.
   *
   * Not a corner case: Esri's own Road Centerlines swatches are solid white —
   * a white-cased road drawn on the map's own ground. Printed straight onto a
   * white legend they are invisible, and the row reads as a missing symbol
   * rather than a pale one.
   */
  pale?: boolean;
  /**
   * Nothing could be resolved for this layer, so the row says so.
   *
   * Deliberately NOT a stand-in symbol. Printing a plausible blue circle for a
   * layer whose real symbology is unknown tells the reader something false,
   * and they have no way to find out it was a guess. An empty frame and a
   * short note is the honest row.
   */
  placeholder?: boolean;
}

export interface LegendGroup {
  name: string;
  swatches: LegendSwatch[];
  /** Classes trimmed by MAX_CLASSES_PER_LAYER, so the sheet can say how many. */
  hiddenClasses?: number;
  /** Set when nothing could be resolved, so the sheet can say so plainly. */
  note?: string;
}

/**
 * Classes printed for any one layer.
 *
 * Not a guess: Esri's own Water_Network_Base_Map publishes 282 symbols for its
 * FacilitySitePoint layer alone. Printed in full, that one layer would fill
 * the sheet and push every other layer off it — so each layer gets a fair
 * share and says how many it left out. A reader who needs all 282 needs the
 * service's own legend, not a paper map.
 */
const MAX_CLASSES_PER_LAYER = 8;

/** Trims a group's classes, recording how many were dropped. */
function capClasses(group: LegendGroup): LegendGroup {
  if (group.swatches.length <= MAX_CLASSES_PER_LAYER) return group;
  return {
    ...group,
    swatches: group.swatches.slice(0, MAX_CLASSES_PER_LAYER),
    hiddenClasses: group.swatches.length - MAX_CLASSES_PER_LAYER,
  };
}

/**
 * Loads an image with CORS, resolving to null rather than throwing.
 *
 * The same reasoning as the map capture: a swatch fetched without CORS taints
 * the canvas, and a tainted canvas refuses `toBlob` — the whole export would
 * fail at the last step over one small picture. A server that does not allow
 * it costs a fallback symbol instead.
 */
export function loadCorsImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

/**
 * What a service swatch actually contains.
 *
 * Worth the pixel read because services publish swatches that cannot be shown
 * as they are, and both cases are indistinguishable from a bug otherwise:
 * a fully transparent image leaves an empty row, and an all-white image leaves
 * one that looks empty. 20×20 is 400 pixels, so this costs nothing.
 *
 * Readable for both sources: ArcGIS swatches arrive as data URIs, and a WMS
 * swatch only ever loads here with `crossOrigin` set, so an image that exists
 * is an image whose pixels can be read.
 */
function inspectSwatch(image: HTMLImageElement): { blank: boolean; pale: boolean } {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth || image.width;
    canvas.height = image.naturalHeight || image.height;
    if (canvas.width === 0 || canvas.height === 0) return { blank: true, pale: false };

    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return { blank: false, pale: false };
    context.drawImage(image, 0, 0);

    const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
    let visible = 0;
    let light = 0;
    for (let index = 0; index < data.length; index += 4) {
      // Nearly-transparent pixels are not ink; antialiasing produces plenty.
      if (data[index + 3] < 24) continue;
      visible += 1;
      if (data[index] > 235 && data[index + 1] > 235 && data[index + 2] > 235) light += 1;
    }

    if (visible === 0) return { blank: true, pale: false };
    // Some services draw a white halo around a coloured mark; only a swatch
    // that is essentially ALL white needs help.
    return { blank: false, pale: light / visible > 0.92 };
  } catch {
    // A tainted canvas cannot be read. The swatch still draws fine.
    return { blank: false, pale: false };
  }
}

/** The renderer's symbol, with one colour swapped in for a unique-value class. */
function symbolFrom(
  renderer: LayerRenderer,
  geometry: LayerGeometryType,
  color: string,
): LegendSymbol {
  return {
    geometry,
    color,
    fillOpacity: renderer.fillOpacity ?? 1,
    outlineColor: renderer.outlineColor ?? '#1f2937',
    outlineWidth: renderer.outlineWidth ?? 1,
    pointSize: renderer.pointSize ?? 10,
    symbolId: renderer.symbolId,
    rotation: renderer.rotation,
    lineStyle: renderer.lineStyle,
    fillStyle: renderer.fillStyle,
    outlineStyle: renderer.outlineStyle,
  };
}

/**
 * Every class of a renderer, as rows.
 *
 * `presentValues`, when given, restricts this to classes a feature was
 * actually seen using within the print extent (see
 * utils/printLegendExtent.ts) — omitted entirely, every class the renderer
 * defines is shown, the original behaviour.
 */
function swatchesFromRenderer(
  renderer: LayerRenderer,
  geometry: LayerGeometryType,
  presentValues?: Set<string>,
): LegendSwatch[] {
  if (renderer.type !== 'unique' || !renderer.categories?.length) {
    // A single symbol needs no class name — the layer's own name is the label.
    return [{ label: '', symbol: symbolFrom(renderer, geometry, renderer.color) }];
  }

  const rows: LegendSwatch[] = renderer.categories
    .filter((category) => !presentValues || presentValues.has(category.value.trim()))
    .map((category) => {
      // Per-category symbol overrides (see RendererCategory) so the legend
      // shows the SAME symbol the map actually drew for this class, not just
      // the renderer's one shared symbol in a different colour.
      const effective = mergeCategoryStyle(renderer, category);
      return {
        // `label` is presentation, `value` the matching key; either can be blank.
        label: category.label || category.value || '(blank)',
        symbol: symbolFrom(effective, geometry, effective.color),
      };
    });

  /* The catch-all class only appears when the renderer actually has one AND
     (if checked) at least one feature actually fell through to it. Printing
     "Other" for a renderer that draws nothing outside its categories — or
     that never needed its catch-all inside this particular extent — would
     claim a symbol nothing on the sheet uses. */
  if (renderer.defaultColor && (!presentValues || presentValues.has(OTHER_CATEGORY))) {
    rows.push({ label: 'Other', symbol: symbolFrom(renderer, geometry, renderer.defaultColor) });
  }
  return rows;
}

/** ArcGIS publishes one legend document for the whole service. */
async function arcgisLegend(baseUrl: string): Promise<Map<number, LegendSwatch[]>> {
  const out = new Map<number, LegendSwatch[]>();
  const response = await fetch(`${baseUrl}/legend?f=json`);
  if (!response.ok) throw new Error(`legend returned ${response.status}`);
  const data = await response.json();

  for (const entry of data?.layers ?? []) {
    const items = entry?.legend ?? [];
    const swatches = await Promise.all(
      items.map(async (item: any) => {
        // Base64 in the response body, so this is a data URI — no CORS
        // question, and nothing extra to fetch.
        const image = item?.imageData
          ? await loadCorsImage(`data:${item.contentType ?? 'image/png'};base64,${item.imageData}`)
          : null;
        if (!image) return null;

        const { blank, pale } = inspectSwatch(image);
        // A swatch with no ink in it describes nothing. Keeping it would print
        // a class name beside an empty box.
        if (blank) return null;
        return { label: item?.label ?? '', image, pale };
      }),
    );
    out.set(entry?.layerId, swatches.filter(Boolean) as LegendSwatch[]);
  }
  return out;
}

/** GeoServer renders one swatch strip per layer. */
function wmsLegendUrl(geoServerUrl: string, layerName: string): string | null {
  try {
    const url = new URL(geoServerUrl.trim());
    url.search = '';
    url.searchParams.set('service', 'WMS');
    url.searchParams.set('version', '1.1.0');
    url.searchParams.set('request', 'GetLegendGraphic');
    url.searchParams.set('format', 'image/png');
    url.searchParams.set('layer', layerName);
    // Without this GeoServer draws its default font and padding, which prints
    // as a grey band around every swatch.
    url.searchParams.set('LEGEND_OPTIONS', 'fontAntiAliasing:true;dpi:120');
    return url.toString();
  } catch {
    return null;
  }
}

/** The row for a layer whose symbology this app cannot know. */
const unknownSwatch: LegendSwatch = { label: '', placeholder: true };

/**
 * True when an ArcGIS layer is drawn by THIS app rather than by the service.
 *
 * A FeatureServer has no `export` endpoint: the app queries it, gets GeoJSON,
 * and draws the vectors itself in the app's own style. A MapServer returns a
 * rendered image, so its published legend is the truth. The service kind is
 * always in the URL, which is why nothing extra has to be threaded in here.
 */
function isClientDrawnArcgis(layer: Layer, options: CollectOptions): boolean {
  if (layer.arcgisLayerId === undefined) return false;
  return /\/FeatureServer/i.test(options.arcgisBaseUrl ?? '');
}

export interface CollectOptions {
  arcgisBaseUrl?: string;
  geoServerUrl?: string;
  /** Display name resolver, so the sheet matches the table of contents. */
  displayName: (layer: Layer) => string;
  /** Restricts a unique-value layer's rows to classes actually present
   *  within the print extent — see utils/printLegendExtent.ts. Keyed by
   *  layer id; a layer with no entry here is unfiltered. */
  presentCategoryValues?: Map<string, Set<string>>;
}

/**
 * Legend content for the layers given, in the order given.
 *
 * Never throws and never rejects: a legend is part of a sheet someone is
 * waiting for, and one unreachable service must not cost them the print.
 * A layer whose symbols cannot be resolved still gets a row, with a note.
 */
export async function collectLegend(
  layers: Layer[],
  options: CollectOptions,
): Promise<LegendGroup[]> {
  /* Fetched once for the service, not once per layer. A map with eight
     sublayers of one MapServer is one request, and the legend document
     describes every layer in it anyway. */
  let arcgis: Map<number, LegendSwatch[]> | null = null;
  const needsArcgis = layers.some(
    (layer) =>
      !layer.renderer &&
      layer.arcgisLayerId !== undefined &&
      !isClientDrawnArcgis(layer, options),
  );
  if (needsArcgis && options.arcgisBaseUrl) {
    try {
      arcgis = await arcgisLegend(options.arcgisBaseUrl);
    } catch {
      arcgis = null;
    }
  }

  const collected: LegendGroup[] = [];
  const groups = {
    push: (group: LegendGroup) => collected.push(capClasses(group)),
  };

  for (const layer of layers) {
    const name = options.displayName(layer);

    // 1. The app's own symbology wins whenever it exists — it is what drew the
    //    map, whether the browser or a generated SLD applied it.
    if (layer.renderer) {
      groups.push({
        name,
        swatches: swatchesFromRenderer(
          layer.renderer,
          layer.geometryType ?? 'unknown',
          options.presentCategoryValues?.get(layer.id),
        ),
      });
      continue;
    }

    /* 2. Client-drawn with no renderer chosen yet, so the map is showing the
          app's DEFAULT style — which the legend can state exactly.

          This has to come before the service branches. An imported file has no
          service at all, and a FeatureServer layer is fetched as GeoJSON and
          drawn here, so asking ArcGIS for its published symbology would answer
          with cartography that is not on screen. Only MapServer, whose image
          the server renders, is genuinely described by its own legend. */
    if (!layer.renderer && (layer.localData || isClientDrawnArcgis(layer, options))) {
      groups.push({
        name,
        swatches: swatchesFromRenderer(DEFAULT_RENDERER, layer.geometryType ?? 'unknown'),
      });
      continue;
    }

    // 3. Otherwise the service's own cartography is on screen.
    if (layer.arcgisLayerId !== undefined) {
      const swatches = arcgis?.get(layer.arcgisLayerId) ?? [];
      groups.push(
        swatches.length > 0
          ? { name, swatches }
          : {
              name,
              swatches: [unknownSwatch],
              note: 'no symbols published',
            },
      );
      continue;
    }

    if (options.geoServerUrl) {
      const url = wmsLegendUrl(options.geoServerUrl, layer.name);
      const image = url ? await loadCorsImage(url) : null;
      const wms = image ? inspectSwatch(image) : { blank: true, pale: false };
      groups.push(
        image && !wms.blank
          ? { name, swatches: [{ label: '', image, pale: wms.pale }] }
          : {
              name,
              swatches: [unknownSwatch],
              note: 'legend image unavailable',
            },
      );
      continue;
    }

    /* Nothing above could speak for this layer: no renderer, no service.
       An honest empty row rather than an invented symbol. */
    groups.push({ name, swatches: [unknownSwatch], note: 'style unknown' });
  }

  return collected;
}
